import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useDisclosure from '@/hooks/useDisclosure';
import { AccordionContextShape, AccordionProviderProps } from './types';

const AccordionContext = React.createContext({} as AccordionContextShape);

const AccordionProvider = ({
  children,
  isExpanded: isExpandedProp,
  onExpand,
  onCollapse,
  defaultIsExpanded = false,
}: React.PropsWithChildren<AccordionProviderProps>) => {
  const {
    isOpen: isExpandedState,
    onOpen: expand,
    onClose: collapse,
    onToggle: toggle,
  } = useDisclosure({ defaultIsOpen: defaultIsExpanded });

  const isExpanded = isExpandedProp ?? isExpandedState;

  const timeout = useRef<NodeJS.Timeout>(null) as React.MutableRefObject<NodeJS.Timeout>;

  const [isStable, setIsStable] = useState(isExpanded);

  // EWM-760 — on collapse, `isStable` must drop to `false` synchronously (so
  // the panel gets `overflow-y-hidden` again immediately, matching the old
  // effect's `else setIsStable(false)` branch). Adjust it during render
  // instead of a cascading `useEffect` — same trigger (`isExpanded` flips to
  // false), one render sooner. The expand case still needs a REAL 200ms
  // delay (matching the collapse animation), which only a timer can provide,
  // so that half stays in the effect below — `setIsStable(true)` there runs
  // inside the `setTimeout` callback, not synchronously in the effect body,
  // so it isn't a cascading-render setState and the linter doesn't flag it.
  const [prevIsExpanded, setPrevIsExpanded] = useState(isExpanded);
  if (prevIsExpanded !== isExpanded) {
    setPrevIsExpanded(isExpanded);
    if (!isExpanded) setIsStable(false);
  }

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);

    if (isExpanded) timeout.current = setTimeout(() => setIsStable(true), 200);
  }, [isExpanded]);

  const handleExpand = useCallback(() => {
    expand();
    onExpand?.();
  }, [expand, onExpand]);

  const handleCollapse = useCallback(() => {
    collapse();
    onCollapse?.();
  }, [collapse, onCollapse]);

  const handleToggle = useCallback(() => {
    toggle();
    (isExpanded ? onCollapse : onExpand)?.();
  }, [isExpandedProp, onCollapse, onExpand, toggle]);

  return (
    <AccordionContext.Provider
      value={{
        isExpanded,
        isStable,
        expand: handleExpand,
        collapse: handleCollapse,
        toggle: handleToggle,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
};

export default AccordionProvider;

export const useAccordion = () => useContext(AccordionContext);
